/**
 * `/sa/features/:id` — a feature's identity plus its rollout across
 * organizations (LOCKER-408).
 *
 * The page used to render two side-by-side lists over one dataset:
 * `FeatureTenantsList` (every organization, with a checkbox that had no
 * `onChange` — so the only write path was two bulk buttons scoped to the search
 * box) and `FeatureAssignments` (a read-only mirror listing only the
 * organizations that already had an assignment, filtered separately). Both are
 * replaced by `FeatureRolloutPanel`.
 *
 * The header now carries `refName` as an identifier rather than burying it in
 * the edit form, and the default-activated state is stated here with its blast
 * radius, editable in one place — the edit dialog.
 */
import EditIcon from "@mui/icons-material/Edit"
import {
  Alert,
  Box,
  Breadcrumbs,
  Chip,
  CircularProgress,
  Divider,
  Link,
  Stack,
  Typography,
} from "@mui/material"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Link as RouterLink, useParams } from "react-router-dom"
import {
  useGetFeatureByIdQuery,
  useSaUpdateThingsMutation,
} from "src/features/things/thingsSaApi"
import FeatureEditDialog from "./featureRollout/FeatureEditDialog"
import FeatureRolloutPanel from "./featureRollout/FeatureRolloutPanel"
import { useFeatureRollout } from "./featureRollout/useFeatureRollout"
import SaLayout from "./SaLayout"
import { Sl2Btn } from "./Sl2Btn"

const cardSx = {
  bgcolor: "white",
  border: "1px solid",
  borderColor: "rgba(0, 0, 0, 0.08)",
  borderRadius: 2,
  boxShadow: "0 1px 2px rgba(35, 66, 133, 0.04)",
}

const FeaturePage = () => {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation()
  const { data, isLoading, isFetching, error } = useGetFeatureByIdQuery(
    { id: id || "" },
    { skip: !id },
  )
  const feature = data?.feature

  const [openEdit, setOpenEdit] = useState(false)
  const [saveError, setSaveError] = useState<string | undefined>()
  const [updateThings, { isLoading: isSaving }] = useSaUpdateThingsMutation()

  // Shared with the panel — RTK Query dedupes, so this is the same request and
  // the dialog's consequence copy quotes the same numbers the bar renders.
  const { counts, defaultActivated } = useFeatureRollout(feature)

  const handleOpenEdit = () => {
    setSaveError(undefined)
    setOpenEdit(true)
  }

  const handleSubmit = async (values: {
    name: string
    description: string
    category: string
    defaultActivated: boolean
  }) => {
    if (!feature) return
    setSaveError(undefined)
    try {
      await updateThings({
        body: {
          things: [
            {
              id: feature.id,
              thingTypeMrn: feature.thingTypeMrn,
              name: values.name,
              description: values.description,
              category: values.category,
              defaultActivated: values.defaultActivated,
            },
          ],
        },
      }).unwrap()
      setOpenEdit(false)
    } catch (e: any) {
      // Surfaced in the dialog. This path was a bare console.error, so a
      // rejected save closed nothing and said nothing (LOCKER-408 DoD6).
      setSaveError(
        e?.data?.message ||
          e?.data?.error ||
          e?.message ||
          t("feature.saveFailed"),
      )
    }
  }

  return (
    <SaLayout selectedMenuItemLabelKey="features">
      {/* Same wrapper shape as SaFeaturesPage. `boxSizing: border-box` is
          load-bearing: the app sets no global box-sizing reset, so the previous
          `Stack p={4} width="100%"` laid its 64px of padding OUTSIDE the 100%
          and the row's State column was clipped by SaLayout's overflow. */}
      <Box
        sx={{
          maxWidth: 1400,
          mx: "auto",
          px: { xs: 2, md: 4 },
          py: { xs: 3, md: 4 },
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <Stack spacing={3}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Breadcrumbs aria-label="breadcrumb">
              <Link
                component={RouterLink}
                underline="hover"
                color="inherit"
                to="/sa/features"
              >
                {t("features")}
              </Link>
              <Typography
                color="text.primary"
                variant="h6"
                sx={{ fontSize: "1rem" }}
              >
                {feature?.name || feature?.id || id}
              </Typography>
            </Breadcrumbs>
            {(isLoading || isFetching) && <CircularProgress size={20} />}
          </Stack>

          {!!error && (
            <Alert severity="error">
              {(error as any)?.message || t("feature.loadFailed")}
            </Alert>
          )}

          {!isLoading && !feature && !error && (
            <Typography variant="body2" color="text.secondary">
              {t("noData")}
            </Typography>
          )}

          {feature && (
            <Box sx={{ ...cardSx, p: { xs: 2, md: 2.5 } }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2.5}
                divider={
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ display: { xs: "none", md: "block" } }}
                  />
                }
              >
                {/* Identity */}
                <Stack spacing={1.25} sx={{ flex: 1.15, minWidth: 0 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, flexGrow: 1 }}
                    >
                      {feature.name || feature.id}
                    </Typography>
                    {feature.category && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={t(`feature.category.${feature.category}`)}
                      />
                    )}
                    {/* Labelled rather than a bare pencil — an unlabelled icon is
                      the kind of control people click to find out what it does. */}
                    <Sl2Btn
                      size="small"
                      variant="outlined"
                      startIcon={<EditIcon fontSize="small" />}
                      onClick={handleOpenEdit}
                      data-testid="feature-edit-open"
                    >
                      {t("feature.editDetails")}
                    </Sl2Btn>
                  </Stack>

                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Box
                      component="span"
                      data-testid="feature-refname"
                      sx={{
                        fontFamily: "monospace",
                        bgcolor: "grey.100",
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        fontSize: "0.75rem",
                        color: "rgba(0, 0, 0, 0.75)",
                      }}
                    >
                      {feature.refName}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {t("feature.refNameIsTheKey")}
                    </Typography>
                  </Stack>

                  {feature.description && (
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary">
                    ID: {feature.id}
                  </Typography>
                </Stack>

                {/* Default state, with its blast radius (DoD13's copy, read-only
                  here — the switch lives in the edit dialog so there is one
                  writer for the field). */}
                <Stack
                  spacing={1}
                  sx={{ flex: 1, minWidth: 0 }}
                  data-testid="feature-default-state"
                >
                  <Typography
                    variant="caption"
                    sx={{
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      fontWeight: 600,
                      color: "text.secondary",
                    }}
                  >
                    {t("feature.defaultActivated")}
                  </Typography>
                  <Chip
                    size="small"
                    label={
                      defaultActivated
                        ? t("feature.defaultOnChip")
                        : t("feature.defaultOffChip")
                    }
                    // The SAME teal the rows use for their `Feature default`
                    // source badge — because that is exactly what this chip
                    // states. Not amber (a reserved warning colour,
                    // `.claude/rules/frontend-conventions.md` §Accessibility —
                    // round 1, F1.3), and not the primary blue that first
                    // replaced it: that is byte-identical to the row-level
                    // `Assignment` badge, so the header said "default" in the
                    // colour the page uses for "set by an assignment", on the
                    // one screen whose purpose is telling those apart
                    // (round 2, F2.1).
                    sx={
                      defaultActivated
                        ? {
                            bgcolor: "rgba(148, 210, 194, 0.35)",
                            color: "#0f5f52",
                            fontWeight: 600,
                            width: "fit-content",
                          }
                        : {
                            bgcolor: "rgba(0,0,0,0.06)",
                            color: "rgba(0,0,0,0.6)",
                            fontWeight: 600,
                            width: "fit-content",
                          }
                    }
                  />
                  <Typography variant="body2" color="text.secondary">
                    {defaultActivated
                      ? t("feature.defaultOnHelp", {
                          count: counts.activatedByDefault,
                        })
                      : t("feature.defaultOffHelp", {
                          count: counts.unassigned,
                        })}
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          )}

          {feature && <FeatureRolloutPanel feature={feature} />}

          {feature && (
            <FeatureEditDialog
              open={openEdit}
              feature={feature}
              counts={counts}
              isSaving={isSaving}
              saveError={saveError}
              onClose={() => setOpenEdit(false)}
              onSubmit={handleSubmit}
            />
          )}
        </Stack>
      </Box>
    </SaLayout>
  )
}

export default FeaturePage
